import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Ant Design Components
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Statistic,
  Space,
  Typography,
  Tag,
  Divider,
  ConfigProvider,
  theme,
  Empty,
  Spin,
  Modal,
  Tooltip
} from 'antd';

// Ant Design Icons
import {
  FileTextOutlined,
  CalendarOutlined,
  BankOutlined,
  DownloadOutlined,
  ReloadOutlined,
  DollarOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import './PayrollReports.css';

// Layout components
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';
import { fetchComboData } from '../../services/api';
import logoImage from '../../assets/logo.png';

const { Title, Text } = Typography;
const { Option } = Select;

const toNumber = (value) => parseFloat(value) || 0;
const asArray = (value) => Array.isArray(value) ? value : [];

const normalizeDepartments = (value) => {
  return asArray(value)
    .map((department, index) => {
      if (department && typeof department === 'object') {
        return {
          ...department,
          id: department.id ?? department.department_id ?? `department-${index}`,
          department_name: department.department_name ?? department.name ?? String(department.id ?? '')
        };
      }

      return {
        id: `department-${index}`,
        department_name: String(department || '')
      };
    })
    .filter((department) => department.department_name);
};

const extractBatchList = (responseData) => {
  if (Array.isArray(responseData?.data?.batches)) {
    return responseData.data.batches;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return asArray(responseData);
};

const PayrollReports = () => {
  // State management
  const [reportType, setReportType] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [fromMonth, setFromMonth] = useState(1);
  const [toMonth, setToMonth] = useState(12);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [departments, setDepartments] = useState([]);
  
  // Salary View Modal States
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryDetailData, setSalaryDetailData] = useState([]);
  const [salaryLoading, setLSalaryLoading] = useState(false);
  const [selectedDeptName, setSelectedDeptName] = useState('');
  const [selectedBatchName, setSelectedBatchName] = useState('');

  // Constants
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  const reportTypes = [
    { value: 'monthly', label: 'Department-wise Monthly Salary', icon: <CalendarOutlined /> },
    { value: 'summary', label: 'Department-wise Total Net Salary', icon: <BankOutlined /> }
  ];

  // Fetch Departments on Component Mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const departmentData = await fetchComboData('employee_departments', 'department_name');
        setDepartments(normalizeDepartments(departmentData));
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch Department-wise Monthly Salary
  const fetchMonthlyReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/payroll/department-salary?payroll_month=${selectedMonth}&payroll_year=${selectedYear}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setPeriodInfo(response.data.payroll_period);
        toast.success('Monthly report loaded successfully');
      } else {
        setReportData([]);
        toast.warning(response.data.message || 'No data found');
      }
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      toast.error('Failed to fetch monthly report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Fetch Department-wise Total Net Salary Summary
  const fetchSummaryReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        payroll_year: selectedYear.toString()
      });

      if (fromMonth && toMonth) {
        params.append('from_month', fromMonth.toString());
        params.append('to_month', toMonth.toString());
      }

      const response = await axios.get(
        `/payroll/department-total-net?${params.toString()}`,
        getHeaders()
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummaryData(response.data.summary);
        setPeriodInfo(response.data.period);
        toast.success('Summary report loaded successfully');
      } else {
        setReportData([]);
        setSummaryData(null);
        toast.warning(response.data.message || 'No data found');
      }
    } catch (error) {
      console.error('Error fetching summary report:', error);
      toast.error('Failed to fetch summary report');
      setReportData([]);
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, fromMonth, toMonth]);

  // Handle Generate Report
  const handleGenerateReport = () => {
    if (reportType === 'monthly') {
      fetchMonthlyReport();
    } else if (reportType === 'summary') {
      fetchSummaryReport();
    }
  };

  // Fetch Salary Details for Selected Batch
  const fetchSalaryDetails = useCallback(async (batchId) => {
    try {
      setLSalaryLoading(true);
      const response = await axios.get(
        `/payroll/records/${batchId}`,
        getHeaders()
      );

      if (response.data.success) {
        setSalaryDetailData(response.data.data || []);
      } else {
        setSalaryDetailData([]);
        toast.warning(response.data.message || 'No salary records found');
      }
    } catch (error) {
      console.error('Error fetching salary details:', error);
      toast.error('Failed to fetch salary details');
      setSalaryDetailData([]);
    } finally {
      setLSalaryLoading(false);
    }
  }, []);

  const resolveBatchId = useCallback(async (record) => {
    const directBatchId = record.batch_id || record.payroll_batch_id;

    if (record.batch_id || record.payroll_batch_id) {
      return directBatchId;
    }

    if (!record.batch_number) {
      return null;
    }

    try {
      const params = new URLSearchParams({
        year: selectedYear.toString(),
        limit: '500'
      });

      if (record.department_id) {
        params.append('department_id', record.department_id.toString());
      }

      const response = await axios.get(`/payroll/batches?${params.toString()}`, getHeaders());
      const batches = extractBatchList(response.data);

      if (!Array.isArray(batches) || batches.length === 0) {
        return null;
      }

      const matchedBatch = batches.find((batch) => {
        return String(batch.batch_number) === String(record.batch_number);
      });

      return matchedBatch?.id || null;
    } catch (error) {
      console.error('Error resolving batch ID:', error);
      return null;
    }
  }, [selectedYear]);

  // Open Salary View Modal
  const openSalaryModal = async (record) => {
    const batchId = await resolveBatchId(record);

    if (!batchId) {
      toast.warning('Batch details are not available for this row');
      return;
    }

    setSelectedDeptName(record.department_name || '');
    setSelectedBatchName(record.batch_number || `Batch ${batchId}`);
    setShowSalaryModal(true);
    fetchSalaryDetails(batchId);
  };

  // Auto-load on component mount
  useEffect(() => {
    handleGenerateReport();
  }, []);

  // Export to Excel with Logo Header
  const exportToExcelWithLogo = () => {
    try {
      if (reportData.length === 0) {
        toast.warning('No data to export');
        return;
      }

      const workbook = XLSX.utils.book_new();
      
      // Create header sheet with logo and title
      const headerSheetData = [
        ['PAYROLL REPORT'],
        [`Generated on: ${new Date().toLocaleDateString()}`],
        [`Period: ${reportType === 'monthly' ? `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}` : `${months.find(m => m.value === fromMonth)?.label} - ${months.find(m => m.value === toMonth)?.label} ${selectedYear}`}`],
        [],
        []
      ];

      const headerSheet = XLSX.utils.aoa_to_sheet(headerSheetData);
      XLSX.utils.book_append_sheet(workbook, headerSheet, 'Summary');

      // Create detailed data sheet
      const worksheet = XLSX.utils.json_to_sheet(reportData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll Report');

      const fileName = reportType === 'monthly'
        ? `Payroll_Monthly_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.xlsx`
        : `Payroll_Summary_${selectedYear}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      toast.success('Report exported to Excel successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export report');
    }
  };

  // Export to PDF with Logo Header
  const exportToPDF = () => {
    try {
      if (reportData.length === 0) {
        toast.warning('No data to export');
        return;
      }

      // Get user information
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let userName = 'Unknown User';
      try {
        if (userStr) {
          const userObj = JSON.parse(userStr);
          userName = userObj.name || userObj.username || userObj.user_name || localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
        } else {
          userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
        }
      } catch (e) {
        userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
      }

      const doc = new jsPDF('landscape');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      const reportTitle = reportType === 'monthly'
        ? `Monthly Report - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
        : `Summary Report - ${selectedYear} (${months.find(m => m.value === fromMonth)?.label} to ${months.find(m => m.value === toMonth)?.label})`;

      // Prepare table columns and data
      const columns = reportType === 'monthly'
        ? ['Department', 'Batch', 'Employees', 'Gross Salary', 'Deductions', 'Net Salary', 'Bonus', 'Overtime', 'Status']
        : ['Department', 'Total Batches', 'Employees', 'Gross Salary', 'Deductions', 'Net Salary', 'Bonus', 'Overtime', 'Completed', 'Draft'];

      const rows = reportData.map(item => [
        item.department_name,
        item.batch_number || item.total_batches || '-',
        item.total_employees,
        item.total_gross_salary?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        item.total_deductions?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        item.total_net_salary?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        item.total_bonus?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        item.total_overtime?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        item.status || item.completed_batches || '-',
        item.draft_batches || '-'
      ]);

      // Add table
      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 25,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [114, 46, 209],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        didDrawPage: function(data) {
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.getHeight();
          const pageWidth = pageSize.getWidth();
          
          // Add header only on first page - COMPACT SINGLE ROW
          if (data.pageNumber === 1) {
            // Add smaller company logo
            doc.addImage(logoImage, 'PNG', 10, 8, 20, 13);

            // Add title and info in single compact row
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('PAYROLL REPORT', pageWidth / 2, 14, { align: 'center' });

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            // Single line with all info including user name
            const headerInfo = `${reportTitle}  |  Generated by: ${userName}  |  ${new Date().toLocaleDateString()}`;
            doc.text(headerInfo, pageWidth / 2, 20, { align: 'center' });
          }
          
          // Footer on all pages
          const totalPages = doc.internal.getNumberOfPages();
          doc.setFontSize(9);
          doc.setTextColor(128, 128, 128);
          doc.text(
            `Page ${data.pageNumber} of ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        },
        margin: { top: 25, right: 10, bottom: 15, left: 10 }
      });

      const fileName = reportType === 'monthly'
        ? `Payroll_Monthly_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.pdf`
        : `Payroll_Summary_${selectedYear}.pdf`;

      doc.save(fileName);
      toast.success('Report exported to PDF successfully');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export report to PDF');
    }
  };

  // Export Salary Details to Excel with Logo Header
  const exportSalaryDetailsToExcel = () => {
    try {
      if (salaryDetailData.length === 0) {
        toast.warning('No salary data to export');
        return;
      }

      const workbook = XLSX.utils.book_new();

      // Create header sheet with logo and title
      const headerSheetData = [
        ['SALARY DETAILS REPORT'],
        [`Department: ${selectedDeptName}`],
        [`Batch: ${selectedBatchName}`],
        [`Month: ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`],
        [`Generated on: ${new Date().toLocaleDateString()}`],
        []
      ];

      const headerSheet = XLSX.utils.aoa_to_sheet(headerSheetData);
      XLSX.utils.book_append_sheet(workbook, headerSheet, 'Summary');

      // Create detailed salary data sheet
      const salaryDataForExport = salaryDetailData.map(emp => ({
        'Employee Name': emp.employee_name,
        'Employee ID': emp.employee_number,
        'Position': emp.position,
        'Payroll Period': emp.payroll_period || '',
        'Gross Salary': emp.gross_salary,
        'Deductions': emp.total_deductions,
        'Net Salary': emp.net_salary,
        'Bonus': emp.bonus,
        'Overtime': emp.overtime,
        'Work Days': emp.work_days,
        'Absent Days': emp.absent_days,
        'Remark': emp.note_remark || '',
        'Signature': ''
      }));

      const salarySheet = XLSX.utils.json_to_sheet(salaryDataForExport);
      XLSX.utils.book_append_sheet(workbook, salarySheet, 'Salary Details');

      const fileName = `Salary_Details_${selectedDeptName}_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success('Salary details exported to Excel successfully');
    } catch (error) {
      console.error('Error exporting salary details to Excel:', error);
      toast.error('Failed to export salary details');
    }
  };

  // Export Salary Details to PDF with Logo Header
  const exportSalaryDetailsToPDF = () => {
    try {
      if (salaryDetailData.length === 0) {
        toast.warning('No salary data to export');
        return;
      }

      // Get user information
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let userName = 'Unknown User';
      try {
        if (userStr) {
          const userObj = JSON.parse(userStr);
          userName = userObj.name || userObj.username || userObj.user_name || localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
        } else {
          userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
        }
      } catch (e) {
        userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown User';
      }

      const doc = new jsPDF('landscape');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Prepare table columns and data
      const columns = ['Employee Name', 'Employee ID', 'Position', 'Payroll Period', 'Gross Salary', 'Deductions', 'Net Salary', 'Bonus', 'Overtime', 'Work Days', 'Absent Days', 'Remark', 'Signature'];

      const rows = salaryDetailData.map(emp => [
        emp.employee_name,
        emp.employee_number,
        emp.position,
        emp.payroll_period || '',
        emp.gross_salary?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        emp.total_deductions?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        emp.net_salary?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        emp.bonus?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        emp.overtime?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || 0,
        emp.work_days || '-',
        emp.absent_days || '-',
        emp.note_remark || '',
        ''
      ]);

      // Add summary row
      const totalGross = salaryDetailData.reduce((sum, emp) => sum + toNumber(emp.gross_salary), 0);
      const totalDeductions = salaryDetailData.reduce((sum, emp) => sum + toNumber(emp.total_deductions), 0);
      const totalNet = salaryDetailData.reduce((sum, emp) => sum + toNumber(emp.net_salary), 0);
      const totalBonus = salaryDetailData.reduce((sum, emp) => sum + toNumber(emp.bonus), 0);
      const totalOvertime = salaryDetailData.reduce((sum, emp) => sum + toNumber(emp.overtime), 0);

      rows.push([
        'TOTAL',
        '',
        '',
        totalGross.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        totalDeductions.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        totalNet.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        totalBonus.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        totalOvertime.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        '',
        '',
        '',
        ''
      ]);

      // Add table
      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 25,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [114, 46, 209],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        didDrawPage: function(data) {
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.getHeight();
          const pageWidth = pageSize.getWidth();
          
          // Add header only on first page - COMPACT SINGLE ROW
          if (data.pageNumber === 1) {
            // Add smaller company logo
            doc.addImage(logoImage, 'PNG', 10, 8, 20, 13);

            // Add title and info in single compact row
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('SALARY DETAILS REPORT', pageWidth / 2, 14, { align: 'center' });

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            // Single line with all info including user name
            const headerInfo = `Department: ${selectedDeptName}  |  Batch: ${selectedBatchName}  |  Period: ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}  |  Generated by: ${userName}  |  ${new Date().toLocaleDateString()}`;
            doc.text(headerInfo, pageWidth / 2, 20, { align: 'center' });
          }
          
          // Footer on all pages
          doc.setFontSize(9);
          doc.setTextColor(128, 128, 128);
          doc.text(
            `Page ${data.pageNumber}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        },
        margin: { top: 25, right: 10, bottom: 15, left: 10 }
      });

      const fileName = `Salary_Details_${selectedDeptName}_${months.find(m => m.value === selectedMonth)?.label}_${selectedYear}.pdf`;
      doc.save(fileName);
      toast.success('Salary details exported to PDF successfully');
    } catch (error) {
      console.error('Error exporting salary details to PDF:', error);
      toast.error('Failed to export salary details to PDF');
    }
  };
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Table columns for Monthly Report
  const monthlyColumns = [
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
      sorter: (a, b) => a.department_name.localeCompare(b.department_name),
      render: (text) => <Tag color="blue" style={{ fontSize: '12px' }}>{text}</Tag>
    },
    {
      title: 'Batch',
      dataIndex: 'batch_number',
      key: 'batch_number',
      align: 'center'
    },
    {
      title: 'Employees',
      dataIndex: 'total_employees',
      key: 'total_employees',
      align: 'center',
      render: (value) => <Tag color="purple">{value}</Tag>
    },
    {
      title: 'Gross Salary',
      dataIndex: 'total_gross_salary',
      key: 'total_gross_salary',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#1890ff' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_gross_salary - b.total_gross_salary
    },
    {
      title: 'Deductions',
      dataIndex: 'total_deductions',
      key: 'total_deductions',
      align: 'right',
      render: (value) => <Text style={{ color: '#ff4d4f' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_deductions - b.total_deductions
    },
    {
      title: 'Net Salary',
      dataIndex: 'total_net_salary',
      key: 'total_net_salary',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_net_salary - b.total_net_salary
    },
    {
      title: 'Bonus',
      dataIndex: 'total_bonus',
      key: 'total_bonus',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Overtime',
      dataIndex: 'total_overtime',
      key: 'total_overtime',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : 'orange'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View salary details for this department">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openSalaryModal(record)}
          >
            View
          </Button>
        </Tooltip>
      )
    }
  ];

  // Table columns for Summary Report
  const summaryColumns = [
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
      sorter: (a, b) => a.department_name.localeCompare(b.department_name),
      render: (text) => <Tag color="blue" style={{ fontSize: '12px' }}>{text}</Tag>
    },
    {
      title: 'Total Batches',
      dataIndex: 'total_batches',
      key: 'total_batches',
      align: 'center',
      render: (value) => <Tag color="cyan">{value}</Tag>
    },
    {
      title: 'Employees',
      dataIndex: 'total_employees',
      key: 'total_employees',
      align: 'center',
      render: (value) => <Tag color="purple">{value}</Tag>
    },
    {
      title: 'Gross Salary',
      dataIndex: 'total_gross_salary',
      key: 'total_gross_salary',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#1890ff' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_gross_salary - b.total_gross_salary
    },
    {
      title: 'Deductions',
      dataIndex: 'total_deductions',
      key: 'total_deductions',
      align: 'right',
      render: (value) => <Text style={{ color: '#ff4d4f' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_deductions - b.total_deductions
    },
    {
      title: 'Net Salary',
      dataIndex: 'total_net_salary',
      key: 'total_net_salary',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total_net_salary - b.total_net_salary
    },
    {
      title: 'Bonus',
      dataIndex: 'total_bonus',
      key: 'total_bonus',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Overtime',
      dataIndex: 'total_overtime',
      key: 'total_overtime',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Completed',
      dataIndex: 'completed_batches',
      key: 'completed_batches',
      align: 'center',
      render: (value) => <Tag color="green">{value}</Tag>
    },
    {
      title: 'Draft',
      dataIndex: 'draft_batches',
      key: 'draft_batches',
      align: 'center',
      render: (value) => <Tag color="orange">{value}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Tooltip title="Batch-level salary details are only available in monthly report">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            disabled
          >
            View
          </Button>
        </Tooltip>
      )
    }
  ];

  return (
    <Layout>
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: '#722ed1',
            borderRadius: 8
          }
        }}
      >
        <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
          {/* Header */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card bordered={false}>
                <Space align="center">
                  <FileTextOutlined style={{ fontSize: 32, color: '#722ed1' }} />
                  <div>
                    <Title level={3} style={{ margin: 0 }}>Payroll Reports</Title>
                    <Text type="secondary">Comprehensive payroll analysis and reporting</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Filters Card */}
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                <span>Report Filters</span>
              </Space>
            }
            bordered={false}
            style={{ marginBottom: 24 }}
          >
            <Row gutter={[16, 16]}>
              {/* Report Type */}
              <Col xs={24} sm={12} md={6}>
                <Text strong>Report Type</Text>
                <Select
                  value={reportType}
                  onChange={setReportType}
                  style={{ width: '100%', marginTop: 8 }}
                  size="large"
                >
                  {reportTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Space>
                        {type.icon}
                        {type.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Year */}
              <Col xs={24} sm={12} md={6}>
                <Text strong>Year</Text>
                <Select
                  value={selectedYear}
                  onChange={setSelectedYear}
                  style={{ width: '100%', marginTop: 8 }}
                  size="large"
                >
                  {years.map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
              </Col>

              {/* Department Selector */}
              <Col xs={24} sm={12} md={6}>
                <Text strong>Department (Optional)</Text>
                <Select
                  placeholder="Select department to view salary"
                  value={selectedDepartment}
                  onChange={setSelectedDepartment}
                  style={{ width: '100%', marginTop: 8 }}
                  size="large"
                  allowClear
                >
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>{dept.department_name}</Option>
                  ))}
                </Select>
              </Col>

              {/* Month (for Monthly Report) */}
              {reportType === 'monthly' && (
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Month</Text>
                  <Select
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    style={{ width: '100%', marginTop: 8 }}
                    size="large"
                  >
                    {months.map(month => (
                      <Option key={month.value} value={month.value}>{month.label}</Option>
                    ))}
                  </Select>
                </Col>
              )}

              {/* From Month (for Summary Report) */}
              {reportType === 'summary' && (
                <Col xs={24} sm={12} md={6}>
                  <Text strong>From Month</Text>
                  <Select
                    value={fromMonth}
                    onChange={setFromMonth}
                    style={{ width: '100%', marginTop: 8 }}
                    size="large"
                  >
                    {months.map(month => (
                      <Option key={month.value} value={month.value}>{month.label}</Option>
                    ))}
                  </Select>
                </Col>
              )}

              {/* To Month (for Summary Report) */}
              {reportType === 'summary' && (
                <Col xs={24} sm={12} md={6}>
                  <Text strong>To Month</Text>
                  <Select
                    value={toMonth}
                    onChange={setToMonth}
                    style={{ width: '100%', marginTop: 8 }}
                    size="large"
                  >
                    {months.map(month => (
                      <Option key={month.value} value={month.value}>{month.label}</Option>
                    ))}
                  </Select>
                </Col>
              )}

              {/* Action Buttons */}
              <Col xs={24} sm={12} md={reportType === 'monthly' ? 3 : 3}>
                <Text strong style={{ opacity: 0 }}>Actions</Text>
                <br />
                <Space wrap style={{ marginTop: 8 }}>
                  <Tooltip title="Generate report">
                    <Button
                      type="primary"
                      size="large"
                      icon={<ReloadOutlined />}
                      onClick={handleGenerateReport}
                      loading={loading}
                    >
                      Generate
                    </Button>
                  </Tooltip>
                  <Tooltip title="Export to Excel with header">
                    <Button
                      size="large"
                      icon={<FileExcelOutlined />}
                      onClick={exportToExcelWithLogo}
                      disabled={reportData.length === 0}
                    >
                      Excel
                    </Button>
                  </Tooltip>
                  <Tooltip title="Export to PDF with logo">
                    <Button
                      size="large"
                      icon={<FilePdfOutlined />}
                      onClick={exportToPDF}
                      disabled={reportData.length === 0}
                      danger
                    >
                      PDF
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Summary Statistics (for Summary Report only) */}
          {reportType === 'summary' && summaryData && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Departments"
                    value={summaryData.total_departments}
                    prefix={<BankOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Employees"
                    value={summaryData.total_employees}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Gross Salary"
                    value={summaryData.total_gross_salary}
                    precision={2}
                    prefix={<RiseOutlined />}
                    suffix="THB"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Net Salary"
                    value={summaryData.total_net_salary}
                    precision={2}
                    prefix={<DollarOutlined />}
                    suffix="THB"
                    valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Deductions"
                    value={summaryData.total_deductions}
                    precision={2}
                    prefix={<FallOutlined />}
                    suffix="THB"
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Bonus"
                    value={summaryData.total_bonus}
                    precision={2}
                    suffix="THB"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Overtime"
                    value={summaryData.total_overtime}
                    precision={2}
                    suffix="THB"
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Other Income"
                    value={summaryData.total_other_income}
                    precision={2}
                    suffix="THB"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Report Table */}
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <span>
                  {reportType === 'monthly'
                    ? `Monthly Report - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
                    : `Summary Report - ${selectedYear} (${months.find(m => m.value === fromMonth)?.label} to ${months.find(m => m.value === toMonth)?.label})`}
                </span>
              </Space>
            }
            bordered={false}
            extra={
              reportData.length > 0 && (
                <Tag color="success">
                  {reportData.length} {reportData.length === 1 ? 'Department' : 'Departments'}
                </Tag>
              )
            }
          >
            <Spin spinning={loading}>
              {reportData.length === 0 && !loading ? (
                <Empty
                  description="No data available"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Text type="secondary">
                    Please select filters and click Generate to view the report
                  </Text>
                </Empty>
              ) : (
                <Table
                  columns={reportType === 'monthly' ? monthlyColumns : summaryColumns}
                  dataSource={reportData}
                  rowKey={(record) => {
                    if (reportType === 'monthly') {
                      return record.batch_id || record.payroll_batch_id || record.batch_number || `${record.department_id}-${record.status}`;
                    }

                    return record.department_id;
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} departments`
                  }}
                  bordered
                  size="small"
                  summary={(pageData) => {
                    if (pageData.length === 0) return null;

                    const totalEmployees = pageData.reduce((sum, row) => sum + (row.total_employees || 0), 0);
                    const totalGross = pageData.reduce((sum, row) => sum + (parseFloat(row.total_gross_salary) || 0), 0);
                    const totalDeductions = pageData.reduce((sum, row) => sum + (parseFloat(row.total_deductions) || 0), 0);
                    const totalNet = pageData.reduce((sum, row) => sum + (parseFloat(row.total_net_salary) || 0), 0);
                    const totalBonus = pageData.reduce((sum, row) => sum + (parseFloat(row.total_bonus) || 0), 0);
                    const totalOvertime = pageData.reduce((sum, row) => sum + (parseFloat(row.total_overtime) || 0), 0);

                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                          <Table.Summary.Cell index={0} colSpan={reportType === 'monthly' ? 2 : 2}>
                            <Text strong>Page Total</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                            <Tag color="purple">{totalEmployees}</Tag>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <Text strong style={{ color: '#1890ff' }}>{formatCurrency(totalGross)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <Text strong style={{ color: '#ff4d4f' }}>{formatCurrency(totalDeductions)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <Text strong style={{ color: '#52c41a' }}>{formatCurrency(totalNet)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">
                            {formatCurrency(totalBonus)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            {formatCurrency(totalOvertime)}
                          </Table.Summary.Cell>
                          {reportType === 'monthly' ? (
                            <Table.Summary.Cell index={8} />
                          ) : (
                            <>
                              <Table.Summary.Cell index={8} />
                              <Table.Summary.Cell index={9} />
                            </>
                          )}
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              )}
            </Spin>
          </Card>
        </div>

        {/* Salary Details Modal */}
        <Modal
          title={`Salary Details - ${selectedDeptName} - ${selectedBatchName}`}
          open={showSalaryModal}
          onCancel={() => setShowSalaryModal(false)}
          width="min(1400px, calc(100vw - 32px))"
          centered
          zIndex={2200}
          rootClassName="payroll-salary-modal-root"
          className="payroll-salary-modal"
          maskStyle={{
            backgroundColor: 'rgba(9, 18, 33, 0.62)',
            backdropFilter: 'blur(2px)'
          }}
          bodyStyle={{
            maxHeight: 'calc(100vh - 220px)',
            overflow: 'auto',
            padding: '0 0 8px'
          }}
          footer={[
            <Button key="close" onClick={() => setShowSalaryModal(false)}>
              Close
            </Button>,
            <Tooltip key="excel" title="Export salary details to Excel with logo header">
              <Button
                icon={<FileExcelOutlined />}
                onClick={exportSalaryDetailsToExcel}
                disabled={salaryDetailData.length === 0}
                type="default"
              >
                Excel
              </Button>
            </Tooltip>,
            <Tooltip key="pdf" title="Export salary details to PDF with logo header">
              <Button
                icon={<FilePdfOutlined />}
                onClick={exportSalaryDetailsToPDF}
                disabled={salaryDetailData.length === 0}
                danger
              >
                PDF
              </Button>
            </Tooltip>
          ]}
        >
          <Spin spinning={salaryLoading}>
            {salaryDetailData.length === 0 ? (
              <Empty
                description="No salary records found"
                style={{ marginTop: 50 }}
              />
            ) : (
              <Table
                  columns={[
                    {
                      title: 'Employee Name',
                      dataIndex: 'employee_name',
                      key: 'employee_name',
                      render: (text) => <Text strong>{text}</Text>
                    },
                    {
                      title: 'Employee ID',
                      dataIndex: 'employee_number',
                      key: 'employee_number',
                      align: 'center'
                    },
                    {
                      title: 'Position',
                      dataIndex: 'position',
                      key: 'position'
                    },
                    {
                      title: 'Payroll Period',
                      dataIndex: 'payroll_period',
                      key: 'payroll_period',
                      align: 'center',
                      render: (value) => value ? <Tag>{value}</Tag> : '-'
                    },
                    {
                      title: 'Gross Salary',
                      dataIndex: 'gross_salary',
                      key: 'gross_salary',
                      align: 'right',
                      render: (value) => <Text style={{ color: '#1890ff' }}>{formatCurrency(value)}</Text>
                    },
                    {
                      title: 'Deductions',
                      dataIndex: 'total_deductions',
                      key: 'total_deductions',
                      align: 'right',
                      render: (value) => <Text style={{ color: '#ff4d4f' }}>{formatCurrency(value)}</Text>
                    },
                    {
                      title: 'Net Salary',
                      dataIndex: 'net_salary',
                      key: 'net_salary',
                      align: 'right',
                      render: (value) => <Text strong style={{ color: '#52c41a', fontSize: '13px' }}>{formatCurrency(value)}</Text>
                    },
                    {
                      title: 'Bonus',
                      dataIndex: 'bonus',
                      key: 'bonus',
                      align: 'right',
                      render: (value) => formatCurrency(value)
                    },
                    {
                      title: 'Overtime',
                      dataIndex: 'overtime',
                      key: 'overtime',
                      align: 'right',
                      render: (value) => formatCurrency(value)
                    }
                  ]}
                  dataSource={salaryDetailData}
                rowKey={(record, index) => `${record.employee_id || record.employee_number || 'employee'}-${record.payroll_period || 'period'}-${index}`}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`
                }}
                size="small"
                summary={(pageData) => {
                  const totalGross = pageData.reduce((sum, row) => sum + toNumber(row.gross_salary), 0);
                  const totalDeductions = pageData.reduce((sum, row) => sum + toNumber(row.total_deductions), 0);
                  const totalNet = pageData.reduce((sum, row) => sum + toNumber(row.net_salary), 0);
                  const totalBonus = pageData.reduce((sum, row) => sum + toNumber(row.bonus), 0);
                  const totalOvertime = pageData.reduce((sum, row) => sum + toNumber(row.overtime), 0);

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Page Total ({pageData.length})</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong style={{ color: '#1890ff' }}>{formatCurrency(totalGross)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong style={{ color: '#ff4d4f' }}>{formatCurrency(totalDeductions)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong style={{ color: '#52c41a' }}>{formatCurrency(totalNet)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <Text strong>{formatCurrency(totalBonus)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} align="right">
                          <Text strong>{formatCurrency(totalOvertime)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
                />
            )}
          </Spin>
        </Modal>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme="light"
        />
      </ConfigProvider>
    </Layout>
  );
};

export default PayrollReports;
